<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup">
            <div class="popup-form">
                <NevsSelect ref="personSelect" v-model="personId" :ajax="'select/persons'"
                            :label="$LANG.Get('labels.person') + ':'"></NevsSelect>
                <div v-if="info !== null" class="check-member-header">
                    <div class="check-member-avatar">
                        <img v-if="info.photo && info.photo.id" :src="info.photo.link" alt=""/>
                        <i v-else class="fa-solid fa-user"></i>
                    </div>
                    <div class="check-member-name">{{ info.first_name }} {{ info.last_name }}</div>
                    <NevsButton class="check-member-open-person" :title="$LANG.Get('tooltips.openInNewWindow')"
                                @click="openPerson()">
                        <i class="fa-solid fa-up-right-from-square"></i> {{ $LANG.Get('buttons.openPerson') }}
                    </NevsButton>
                </div>
                <div v-if="info !== null" class="check-member-info">
                    <div v-if="info.is_paused" class="check-member-paused">
                        <i class="fa-solid fa-circle-pause"></i>
                        {{ $LANG.Get('labels.personPaused') }}
                    </div>
                    <div v-if="info.note" class="check-member-note">
                        <i class="fa-solid fa-note-sticky"></i>
                        <span class="check-member-note-text">{{ info.note }}</span>
                    </div>
                    <div v-if="info.birthday_today" class="check-member-birthday">
                        <i class="fa-solid fa-cake-candles"></i> {{ $LANG.Get('labels.birthdayToday') }}
                    </div>
                    <div v-if="info.is_holiday" class="check-member-holiday">
                        <i class="fa-solid fa-calendar-day"></i> {{ $LANG.Get('labels.holidayToday') }}
                    </div>
                    <template v-if="info.active_memberships.length > 0">
                        <div v-for="(membership, key) in info.active_memberships" :key="key"
                             class="check-member-membership-item">
                            <div class="check-member-membership check-member-membership-active">
                                <i class="fa-solid fa-id-card"></i>
                                {{ $LANG.Get('labels.activeMembership') }}:
                                <strong>{{ membership.name }}</strong>
                                ({{ membership.start_date_display }} - {{ membership.end_date_display }})
                                <div class="check-member-entries">
                                    {{ $LANG.Get('fields.entries') }}: <strong>{{ entriesText(membership) }}</strong>
                                </div>
                            </div>
                            <!-- Only for an entry that will count against this membership: a check-in at a time it allows. -->
                            <div v-if="entryWarning(membership) !== ''" class="check-member-entries-warning">
                                <i class="fa-solid fa-triangle-exclamation"></i>
                                {{ entryWarning(membership) }}
                            </div>
                            <div v-if="!membership.time_valid" class="check-member-time-invalid">
                                <div>
                                    <i class="fa-solid fa-triangle-exclamation"></i>
                                    {{ $LANG.Get('labels.membershipTimeInvalid') }}
                                </div>
                                <div class="check-member-time-valid-hours">
                                    <template v-if="membership.today_intervals.length > 0">
                                        {{ $LANG.Get('labels.allowedTimesToday') }}
                                        {{ membership.today_intervals.map((interval) => interval.from + ' - ' + interval.to).join(', ') }}
                                    </template>
                                    <template v-else>
                                        {{ $LANG.Get('labels.noEntryToday') }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div v-else class="check-member-membership check-member-membership-none">
                        <i class="fa-solid fa-circle-exclamation"></i>
                        {{ $LANG.Get('labels.noActiveMembership') }}
                    </div>
                    <div class="check-member-presence">
                        <template v-if="info.is_inside">
                            <i class="fa-solid fa-right-from-bracket"></i>
                            {{ $LANG.Get('labels.willBeCheckedOut') }}
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-right-to-bracket"></i>
                            {{ $LANG.Get('labels.willBeCheckedIn') }}
                            <div class="check-member-last-visit">
                                {{ $LANG.Get('labels.lastVisit') }}:
                                <strong>{{ info.last_visit_display !== '' ? info.last_visit_display : $LANG.Get('labels.noVisits') }}</strong>
                            </div>
                        </template>
                    </div>
                    <div v-if="!info.is_inside && localProducts.length > 0" class="check-member-products">
                        <div class="check-member-products-title">{{ $LANG.Get('labels.selectProductsToUse') }}</div>
                        <table class="check-member-products-table">
                            <thead>
                            <tr>
                                <th class="check-member-products-check"></th>
                                <th>{{ $LANG.Get('fields.product') }}</th>
                                <th>{{ $LANG.Get('labels.available') }}</th>
                                <th class="check-member-products-quantity">{{ $LANG.Get('fields.quantity') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(product, key) in localProducts" :key="key">
                                <td><NevsCheckbox v-model="product.selected"></NevsCheckbox></td>
                                <td>{{ product.product_name }}</td>
                                <td>{{ product.quantity_display }}</td>
                                <td>
                                    <NevsNumberField v-if="product.selected" :decimal-places="2"
                                                     v-model="product.quantity" :error="product.error"></NevsNumberField>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancelClick()">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.cancel') }}
                </NevsButton>
                <NevsButton class="success" @click="okClick()">
                    <i class="fa-solid fa-check"></i>
                    {{ $LANG.Get('buttons.ok') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";

export default {
    name: "ModalCheckMember",
    components: {NevsNumberField, NevsCheckbox, NevsSelect, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        // When set (a person id), the modal opens with that person already selected instead of the empty
        // picker. Used when a member requests entry from the members app.
        preselectPersonId: {
            type: Number,
            default: null
        }
    },
    emits: ['done'],
    data() {
        return {
            loaded: false,
            personId: null,
            info: null,
            localProducts: []
        }
    },
    watch: {
        personId() {
            this.info = null;
            this.localProducts = [];
            if (this.personId !== null) {
                let vm = this;
                this.$API.APICall('get', 'persons/check-in-info/' + this.personId, {}, (data, success) => {
                    if (success) {
                        vm.info = data;
                        vm.localProducts = data.products.map((product) => ({
                            product_id: product.product_id,
                            product_name: product.product_name,
                            quantity_display: product.quantity_display,
                            quantity_available: product.quantity,
                            selected: false,
                            quantity: 1,
                            error: ''
                        }));
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                });
            }
        },
        show() {
            this.unbindSearchEnter();
            this.loaded = false;
            // Start on the preselected person when one was passed, otherwise an empty picker.
            this.personId = this.show ? this.preselectPersonId : null;
            this.info = null;
            this.localProducts = [];
            if (this.show) {
                this.$nextTick(() => {
                    this.loaded = true;
                    this.$nextTick(() => {
                        this.bindSearchEnter();
                        // Auto-open the person picker only for a manual check-in; a preselected person needs no picking.
                        if (this.preselectPersonId === null && this.$refs.personSelect) {
                            this.$refs.personSelect.dropdownClick();
                        }
                    });
                });
            }
        },
        // If a (new) person is preselected while the modal is already open, just switch the selection to them.
        preselectPersonId() {
            if (this.show && this.preselectPersonId !== null) {
                this.personId = this.preselectPersonId;
            }
        }
    },
    created() {
        // Deliberately not in data(): these hold a DOM node and a listener, which must not be made reactive.
        this.searchField = null;
        this.searchEnterHandler = null;
    },
    methods: {
        // Enter in the person picker's search field picks the first person listed. NevsSelect is shared by the
        // whole app, so the shortcut is attached from here rather than built into it: its search input is
        // reachable through the ref, and this does exactly what clicking that first option does - select it,
        // then let the field's focusout close the dropdown.
        //
        // The list Enter acts on is always the one on screen: NevsSelect filters the options it has loaded by
        // the typed text itself, so the first match is right even while its debounced server search is still
        // in flight.
        bindSearchEnter() {
            let select = this.$refs.personSelect;
            if (!select || !select.$refs.searchField) return;

            this.searchField = select.$refs.searchField;
            this.searchEnterHandler = (event) => {
                if (event.key !== 'Enter') return;
                // A nullable select lists its "---" entry first; that is never a person to pick.
                let option = (select.filteredOptions || []).find((candidate) => candidate.value !== null);
                if (option === undefined) return;
                event.preventDefault();
                select.selectOption(option);
                this.searchField.blur();
            };
            this.searchField.addEventListener('keydown', this.searchEnterHandler);
        },
        unbindSearchEnter() {
            if (this.searchField !== null && this.searchEnterHandler !== null) {
                this.searchField.removeEventListener('keydown', this.searchEnterHandler);
            }
            this.searchField = null;
            this.searchEnterHandler = null;
        },
        // The selected person's own screen, opened in a new browser tab so the modal - and the check-in the
        // user is in the middle of - stays exactly as it is.
        // "7 / 10" for a membership with an entry limit, just the count for an unlimited one.
        entriesText(membership) {
            return membership.max_entries > 0
                ? membership.entries_used + ' / ' + membership.max_entries
                : String(membership.entries_used);
        },
        entryWarning(membership) {
            if (this.info.is_inside || !membership.time_valid || !(membership.max_entries > 0)) return '';
            if (membership.entries_used >= membership.max_entries) return this.$LANG.Get('labels.entriesUsedUpWarning');
            if (membership.entries_used + 1 === membership.max_entries) return this.$LANG.Get('labels.lastEntryWarning');
            return '';
        },
        openPerson() {
            if (this.personId === null) return;
            window.open(this.$router.resolve('/persons/' + this.personId).href);
        },
        cancelClick() {
            this.$emit('done', false);
        },
        okClick() {
            if (this.personId === null) return;

            let products = [];
            if (this.info !== null && !this.info.is_inside) {
                let valid = true;
                for (let product of this.localProducts) {
                    product.error = '';
                    if (!product.selected) continue;
                    if (product.quantity === null || product.quantity <= 0 || product.quantity > product.quantity_available) {
                        product.error = this.$LANG.Get('labels.notEnoughAvailable');
                        valid = false;
                        continue;
                    }
                    products.push({product_id: product.product_id, quantity: product.quantity});
                }
                if (!valid) return;
            }

            let vm = this;
            this.$API.APICall('post', 'visits/toggle', {person_id: this.personId, products: products}, (data, success) => {
                if (success) {
                    vm.$emit('done', true);
                } else if (data.error === 'insufficient_products') {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.insufficientProducts'), type: 'alert'});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    unmounted() {
        this.unbindSearchEnter();
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.nevs-popup {
    max-width: 500px;
}

.popup-form {
    padding: 10px;
}

.check-member-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 15px;
}

.check-member-avatar {
    flex: 0 0 auto;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    overflow: hidden;
    background: $sand-100;
    border: 2px solid $sand-300;
    display: flex;
    align-items: center;
    justify-content: center;
}

.check-member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.check-member-avatar i {
    font-size: 28px;
    color: $ink-muted;
}

.check-member-name {
    font-size: 1.1em;
    font-weight: 600;
}

/* Pushed to the far end of the header row, opposite the avatar and the name. */
.check-member-open-person {
    margin-left: auto;
    flex-shrink: 0;
}

.check-member-info {
    margin-top: 15px;
}

.check-member-birthday {
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
    font-weight: 600;
}

.check-member-holiday {
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
    font-weight: 600;
}

/* A paused person is refused at the doors, so it has to read louder than the memberships listed below it. */
.check-member-paused {
    background-color: $danger-surface;
    border: 1px solid $danger-border;
    color: $danger;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
    font-weight: 600;
}

.check-member-note {
    display: flex;
    gap: 8px;
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 10px;
}

.check-member-note i {
    margin-top: 2px;
    flex-shrink: 0;
}

.check-member-note-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.check-member-membership-item:not(:first-child) {
    margin-top: 10px;
}

.check-member-membership {
    background-color: $sand-100;
    border: 1px solid $sand-300;
    border-radius: 5px;
    padding: 10px 12px;
}

/* Green when the person has an active membership, red when they have none. */
.check-member-membership-active {
    background-color: $success-surface;
    border-color: $success;
    color: $ink-strong;
}

.check-member-membership-none {
    background-color: $danger-surface;
    border-color: $danger-border;
    color: $danger;
    font-weight: 600;
}

.check-member-presence {
    background-color: $sand-100;
    border: 1px solid $sand-300;
    border-radius: 5px;
    padding: 10px 12px;
    margin-top: 10px;
}

.check-member-last-visit {
    margin-top: 6px;
}

.check-member-time-invalid {
    background-color: $danger-surface;
    border: 1px solid $danger-border;
    color: $danger;
    border-radius: 5px;
    padding: 10px 12px;
    margin-top: 10px;
    font-weight: 600;
}

.check-member-entries {
    margin-top: 4px;
}

.check-member-entries-warning {
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 10px 12px;
    margin-top: 10px;
    font-weight: 600;
}

.check-member-time-valid-hours {
    margin-top: 6px;
    font-weight: 400;
    font-size: 0.9em;
}

.check-member-products {
    margin-top: 10px;
    border: 1px solid $sand-300;
    border-radius: 5px;
    padding: 10px 12px;
}

.check-member-products-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.check-member-products-table {
    width: 100%;
    border-collapse: collapse;
}

.check-member-products-table th, .check-member-products-table td {
    text-align: left;
    padding: 5px 10px 5px 0;
    vertical-align: middle;
}

.check-member-products-table th {
    font-size: 0.85em;
}

.check-member-products-check {
    width: 30px;
}

.check-member-products-quantity {
    width: 110px;
}
</style>
